// src/RootComponent.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { Box, MantineProvider } from "@mantine/core";
import App from "./App";
import { DataProvider } from "./context/DataContext";
import NotFound from "./pages/not-found";
import SubRoutes from "./routes";
import {
  FpjsProvider,
  FingerprintJSPro,
} from "@fingerprintjs/fingerprintjs-pro-react";
import * as Sentry from "@sentry/react"; // Import Sentry

// Sentry is currently disabled
// To re-enable, uncomment the Sentry initialization below and the SentryRoutes variable
/*
Sentry.init({
  dsn: "https://e5bbbf0db439485e92518f5484e1baae@o4504792099258368.ingest.us.sentry.io/4504792101158912",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration(),
  ],
  tracesSampleRate: 1.0, // Adjust based on your need
  tracePropagationTargets: [
    /^https:\/\/fe\.dcams\.app/, // Match fe.dcams.app
    /^https:\/\/vx-fe\.dcams\.app/, // Match vx-fe.dcams.app
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
*/

// const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Function to detect system color scheme preference
function getSystemColorScheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

// Function to get request_id from session_id
async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

const AppWithData = () => {
  const { request_id } = useParams();
  const location = useLocation();
  // Track renders
  const renderCount = React.useRef(0);
  renderCount.current++;
  console.log('🏠 AppWithData render', {
    renderCount: renderCount.current,
    request_id,
    timestamp: new Date().toISOString()
  });

  // Check fingerprint param directly in the render function to avoid timing issues
  const urlParams = new URLSearchParams(location.search);
  const fingerprintParam = urlParams.get('fingerprint');
  const shouldDisableFingerprint = fingerprintParam === 'f';

  
  // Sentry tag setting is currently disabled
  /*
  useEffect(() => {
    if (request_id) {
      // Set request_id as a tag in Sentry
      Sentry.setTag("request_id", request_id);
    }
  }, [request_id]);
  */

  // Session fetching is now handled entirely in DataContext

  // Determine if fingerprinting should be disabled based on URL param
  const fingerprintDisabled = shouldDisableFingerprint;

  // Use the actual color scheme from data, or system as fallback
  // Use system color scheme initially - DataContext will handle theme updates if needed
  const themeColorScheme = getSystemColorScheme();
  
  // Always render the same structure to prevent remounting
  const dataProviderContent = (
    <DataProvider 
      key={`data-provider-${request_id}`} // Stable key based on request_id
      request_id={request_id} 
      fingerprintDisabled={fingerprintDisabled}
      // No longer passing session data - DataContext fetches it directly
    >
      <App routes={<SubRoutes />} />
    </DataProvider>
  );

  // Skip FpjsProvider when disabled via URL param or settings
  if (fingerprintDisabled) {
    console.log('🎨 Root: Rendering DataProvider (no fingerprint)');
    
    return (
      <MantineProvider theme={{ colorScheme: themeColorScheme }} withGlobalStyles withNormalizeCSS>
        {dataProviderContent}
      </MantineProvider>
    );
  }

  // Regular flow with fingerprinting
  console.log('🎨 Root: Rendering DataProvider (with fingerprint)');
  
  return (
    <MantineProvider theme={{ colorScheme: themeColorScheme }} withGlobalStyles withNormalizeCSS>
      <FpjsProvider
        loadOptions={{
          apiKey: "U1OoU5FUoWuUW1gJvyL3",
          endpoint: [
            "https://fingerprint.dcams.app",
            FingerprintJSPro.defaultEndpoint,
          ],
          scriptUrlPattern: [
            "https://fingerprint.dcams.app/web/v<version>/<apiKey>/loader_v<loaderVersion>.js",
            FingerprintJSPro.defaultScriptUrlPattern,
          ],
        }}
      >
        {dataProviderContent}
      </FpjsProvider>
    </MantineProvider>
  );
};

const RootComponent = () => {
  return (
    <Router>
      {/* Sentry error boundary is currently disabled
      <Sentry.ErrorBoundary fallback={<div>An error has occurred.</div>}>
      */}
        <Routes>
          <Route path="/:request_id/*" element={<AppWithData />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      {/* </Sentry.ErrorBoundary> */}
    </Router>
  );
};

export default RootComponent;
